import { raw, esc } from '../lib/html.js';
import { skyAt } from '../lib/content.js';

// The signature illustration: a night sky over the plain of Kurukshetra that
// turns to dawn as the learner progresses. At 0% the sun sits on the horizon
// under a sky full of stars; by the end of chapter 18 it is full morning.
//
// Built in layers so it stays correct at every width: a CSS sky gradient, a
// star field that may crop, a sun of fixed size positioned in CSS, and hills
// that stretch horizontally. Sizes are tuned per use with CSS variables
// (--ground, --hills, --rise) on the parent class.

let uid = 0;

// Deterministic "random" so the stars never jump between renders.
function rand(seed) {
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  return x - Math.floor(x);
}

function stars(count, seedBase, opacity) {
  if (opacity <= 0) return '';
  let dots = '';
  for (let i = 0; i < count; i++) {
    const x = rand(i + seedBase) * 800;
    const y = rand(i + seedBase + 1000) * 300;
    const r = 0.6 + rand(i + seedBase + 2000) * 1.1;
    const delay = (rand(i + seedBase + 3000) * 4).toFixed(2);
    dots += `<circle class="dawn__star" cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r.toFixed(2)}" style="animation-delay:${delay}s"/>`;
  }
  return `<svg class="dawn__stars" viewBox="0 0 800 300" preserveAspectRatio="xMidYMin slice" aria-hidden="true"><g fill="#fff8e6" opacity="${opacity.toFixed(2)}">${dots}</g></svg>`;
}

export function dawnScene(progress, { className = '', label = '' } = {}) {
  const p = Math.min(1, Math.max(0, progress || 0));
  const id = `dawn${++uid}`;
  const a = skyAt(p * 0.82);
  const b = skyAt(Math.min(1, p * 0.82 + 0.16));
  const c = skyAt(Math.min(1, p * 0.82 + 0.34));
  const far = skyAt(Math.max(0, p * 0.6 - 0.05));
  const a11y = label ? `role="img" aria-label="${esc(label)}"` : 'aria-hidden="true"';
  return raw(`<div class="dawn ${esc(className)}" style="--p:${p.toFixed(3)};--sky-a:${a};--sky-b:${b};--sky-c:${c}" ${a11y}>
  ${stars(70, 1, Math.max(0, 1 - p * 2.4))}
  <div class="dawn__sunrise"><div class="dawn__glow"></div><div class="dawn__sun"></div></div>
  <svg class="dawn__hills" viewBox="0 0 400 60" preserveAspectRatio="none" aria-hidden="true">
    <defs>
      <linearGradient id="${id}-far" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${far}" stop-opacity="0.92"/><stop offset="1" stop-color="#1a1330"/></linearGradient>
      <linearGradient id="${id}-near" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#140f24"/><stop offset="1" stop-color="#0b0816"/></linearGradient>
    </defs>
    <path d="M0 22 C 50 8, 95 6, 140 14 S 238 26, 286 14 S 360 2, 400 10 L 400 60 L 0 60 Z" fill="url(#${id}-far)"/>
    <path d="M0 36 C 70 24, 132 30, 204 34 S 330 26, 400 32 L 400 60 L 0 60 Z" fill="url(#${id}-near)"/>
  </svg>
  <div class="dawn__ground"></div>
</div>`);
}

// Stars and a soft sun glow for a sky-coloured panel (chapter openings).
// f is the position in the journey: 0 = deep night, 1 = full morning.
export function skyDecor(f) {
  const id = `sky${++uid}`;
  return raw(`<div class="sky-decor" aria-hidden="true">
    <svg viewBox="0 0 400 400" preserveAspectRatio="xMidYMin slice" width="100%" height="100%">
      <defs><radialGradient id="${id}" cx="0.82" cy="0.08" r="0.7"><stop offset="0" stop-color="#fff1cf" stop-opacity="${(0.12 + f * 0.55).toFixed(2)}"/><stop offset="1" stop-color="#fff1cf" stop-opacity="0"/></radialGradient></defs>
      <rect width="400" height="400" fill="url(#${id})"/>
    </svg>
    ${stars(40, 500, Math.max(0, 1 - f * 1.9))}
  </div>`);
}
